'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  type DownloadManagerDockBounds,
  type DownloadManagerDockSafeInsets,
} from '@/lib/download-manager-docking';
import {
  clampFloatingVoiceDockPoint,
  getFloatingVoiceDockDefaultPosition,
  getFloatingVoiceDockTarget,
  type FloatingVoiceDockPosition,
} from '@/lib/floating-voice-dock-state';
import {
  FOLDER_UPLOADS_TRAY_SELECTOR,
  observeDownloadManagerGeometry,
} from '@/lib/observe-download-manager-geometry';
import {
  getMobileVoiceComposerBottomInset,
  getVoiceComposerBounds,
  observeVoiceComposerGeometry,
} from '@/lib/floating-voice-composer-clearance';
import { isDockedBesideArtifactPanel } from '@/lib/floating-assistant-inset';

const DRAG_THRESHOLD = 6;
// Reserve the top navigation row; the docking margin also clears avatar motion.
const NAVIGATION_CLEARANCE = 32;
const savedPositions = new Map<string, { x: number; y: number }>();

function getDeviceSafeInsets(): DownloadManagerDockSafeInsets {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  document.body.append(probe);
  const style = window.getComputedStyle(probe);
  const insets = {
    top: (Number.parseFloat(style.paddingTop) || 0) + NAVIGATION_CLEARANCE,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

/** The downloads panel and the upload tray, each a rect to step around. */
function getDockObstacles(): DownloadManagerDockBounds[] {
  return [
    '[data-slot="download-manager-boundary"] [data-dock-corner]',
    FOLDER_UPLOADS_TRAY_SELECTOR,
  ]
    .map((selector) => document.querySelector(selector))
    .map((element) => element?.getBoundingClientRect())
    .filter((rect): rect is DOMRect => !!rect && rect.width > 0)
    .map(({ left, top, width, height }) => ({ left, top, width, height }));
}

export function useFloatingVoiceDockPosition(
  isSourceChatVisible: boolean,
  {
    enabled = true,
    positionKey,
    avoidMobileComposer = false,
  }: {
    enabled?: boolean;
    positionKey?: string;
    avoidMobileComposer?: boolean;
  } = {}
) {
  const boundaryRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const getSafeInsets = useCallback(() => {
    const insets = getDeviceSafeInsets();
    const boundary = boundaryRef.current;
    if (!avoidMobileComposer || !boundary) return insets;
    return {
      ...insets,
      bottom: getMobileVoiceComposerBottomInset({
        viewportWidth: window.innerWidth,
        boundary: boundary.getBoundingClientRect(),
        composers: getVoiceComposerBounds(),
        safeBottom: insets.bottom,
        dockedBesidePanel: isDockedBesideArtifactPanel(),
      }),
    };
  }, [avoidMobileComposer]);
  const defaultPosition =
    getFloatingVoiceDockDefaultPosition(isSourceChatVisible);
  const [position, setPosition] = useState<
    FloatingVoiceDockPosition | 'custom'
  >(defaultPosition);
  const positionRef = useRef<FloatingVoiceDockPosition | 'custom'>(
    defaultPosition
  );
  const [point, setPoint] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const [canAnimatePosition, setCanAnimatePosition] = useState(false);
  const [dragging, setDragging] = useState(false);
  const restoredPositionKeyRef = useRef<string | undefined>(undefined);
  const pointerRef = useRef<{
    id: number;
    target: HTMLButtonElement;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const suppressClickFrameRef = useRef<number | null>(null);

  const finishDrag = useCallback(() => {
    const pointer = pointerRef.current;
    if (!pointer) return;
    // Clear first because releasing capture can dispatch another finish event.
    pointerRef.current = null;
    setDragging(false);
    const boundary = boundaryRef.current;
    const panel = panelRef.current;
    if (boundary && panel) {
      const safeInsets = getSafeInsets();
      setPoint((current) =>
        clampFloatingVoiceDockPoint(
          current,
          { width: boundary.clientWidth, height: boundary.clientHeight },
          { width: panel.offsetWidth, height: panel.offsetHeight },
          safeInsets
        )
      );
    }
    if (pointer.moved) {
      positionRef.current = 'custom';
      setPosition('custom');
      suppressClickRef.current = true;
      if (suppressClickFrameRef.current !== null) {
        window.cancelAnimationFrame(suppressClickFrameRef.current);
      }
      suppressClickFrameRef.current = window.requestAnimationFrame(() => {
        suppressClickRef.current = false;
        suppressClickFrameRef.current = null;
      });
    }
    if (pointer.target.hasPointerCapture(pointer.id)) {
      pointer.target.releasePointerCapture(pointer.id);
    }
  }, [getSafeInsets]);

  useEffect(() => {
    if (!enabled) return;
    const onPointerEnd = (event: PointerEvent) => {
      if (pointerRef.current?.id === event.pointerId) finishDrag();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') finishDrag();
    };
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerEnd, true);
    window.addEventListener('lostpointercapture', onPointerEnd, true);
    window.addEventListener('blur', finishDrag);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pointerup', onPointerEnd, true);
      window.removeEventListener('pointercancel', onPointerEnd, true);
      window.removeEventListener('lostpointercapture', onPointerEnd, true);
      window.removeEventListener('blur', finishDrag);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const pointer = pointerRef.current;
      pointerRef.current = null;
      if (pointer?.target.hasPointerCapture(pointer.id)) {
        pointer.target.releasePointerCapture(pointer.id);
      }
      if (suppressClickFrameRef.current !== null) {
        window.cancelAnimationFrame(suppressClickFrameRef.current);
      }
    };
  }, [enabled, finishDrag]);

  const dock = useCallback(
    (preferred: FloatingVoiceDockPosition) => {
      const panel = panelRef.current;
      const boundary = boundaryRef.current;
      if (!panel || !boundary) return;
      const target = getFloatingVoiceDockTarget(preferred, {
        boundary: boundary.getBoundingClientRect(),
        panel: { width: panel.offsetWidth, height: panel.offsetHeight },
        obstacles: getDockObstacles(),
        safeInsets: getSafeInsets(),
      });
      setPoint((current) =>
        current.x === target.x && current.y === target.y
          ? current
          : { x: target.x, y: target.y }
      );
      setReady(true);
    },
    [getSafeInsets]
  );

  useEffect(() => {
    if (!enabled) return;
    const frame = window.requestAnimationFrame(() => {
      const saved = positionKey ? savedPositions.get(positionKey) : undefined;
      const custom =
        Boolean(saved) ||
        (restoredPositionKeyRef.current === positionKey &&
          positionRef.current === 'custom');
      restoredPositionKeyRef.current = positionKey;
      const boundary = boundaryRef.current;
      const panel = panelRef.current;
      if (custom && boundary && panel) {
        positionRef.current = 'custom';
        setPosition('custom');
        setPoint((current) =>
          clampFloatingVoiceDockPoint(
            saved ?? current,
            { width: boundary.clientWidth, height: boundary.clientHeight },
            { width: panel.offsetWidth, height: panel.offsetHeight },
            getSafeInsets()
          )
        );
        setReady(true);
        return;
      }
      positionRef.current = defaultPosition;
      setPosition(defaultPosition);
      dock(defaultPosition);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [defaultPosition, dock, enabled, positionKey, getSafeInsets]);

  useEffect(() => {
    if (
      enabled &&
      positionKey &&
      restoredPositionKeyRef.current === positionKey &&
      position === 'custom'
    )
      savedPositions.set(positionKey, point);
  }, [enabled, point, position, positionKey]);

  useEffect(() => {
    if (!enabled || !ready) return;
    // Paint the measured position before allowing later docking transitions.
    const frame = window.requestAnimationFrame(() =>
      setCanAnimatePosition(true)
    );
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, ready]);

  useEffect(() => {
    if (!enabled) return;
    const panel = panelRef.current;
    const boundary = boundaryRef.current;
    if (!panel || !boundary) return;
    const update = () => {
      if (pointerRef.current) return;
      const currentPosition = positionRef.current;
      if (currentPosition === 'custom') {
        setPoint((current) =>
          clampFloatingVoiceDockPoint(
            current,
            {
              width: boundary.clientWidth,
              height: boundary.clientHeight,
            },
            { width: panel.offsetWidth, height: panel.offsetHeight },
            getSafeInsets()
          )
        );
      } else {
        dock(currentPosition);
      }
    };
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    observer.observe(boundary);
    const stopObservingDownloads = observeDownloadManagerGeometry(update);
    const stopObservingComposer = avoidMobileComposer
      ? observeVoiceComposerGeometry(update)
      : undefined;
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      observer.disconnect();
      stopObservingDownloads();
      stopObservingComposer?.();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [dock, enabled, avoidMobileComposer, getSafeInsets]);

  const onDragPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (
        !enabled ||
        event.button !== 0 ||
        pointerRef.current ||
        event.isPrimary === false
      )
        return;
      pointerRef.current = {
        id: event.pointerId,
        target: event.currentTarget,
        startX: event.clientX,
        startY: event.clientY,
        originX: point.x,
        originY: point.y,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [enabled, point.x, point.y]
  );

  const onDragPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.id !== event.pointerId) return;
      if ((event.buttons & 1) === 0) {
        finishDrag();
        return;
      }
      const dx = event.clientX - pointer.startX;
      const dy = event.clientY - pointer.startY;
      if (!pointer.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      pointer.moved = true;
      setDragging(true);
      const boundary = boundaryRef.current;
      const panel = panelRef.current;
      if (!boundary || !panel) return;
      setPoint(
        clampFloatingVoiceDockPoint(
          { x: pointer.originX + dx, y: pointer.originY + dy },
          { width: boundary.clientWidth, height: boundary.clientHeight },
          { width: panel.offsetWidth, height: panel.offsetHeight },
          getSafeInsets()
        )
      );
    },
    [finishDrag, getSafeInsets]
  );

  const onDragPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.id !== event.pointerId) return;
      finishDrag();
    },
    [finishDrag]
  );

  const moveByKey = useCallback(
    (key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight') => {
      if (!enabled) return;
      const boundary = boundaryRef.current;
      const panel = panelRef.current;
      if (!boundary || !panel) return;
      const step = 24;
      const dx = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
      const dy = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;
      positionRef.current = 'custom';
      setPosition('custom');
      setPoint((current) =>
        clampFloatingVoiceDockPoint(
          { x: current.x + dx, y: current.y + dy },
          { width: boundary.clientWidth, height: boundary.clientHeight },
          { width: panel.offsetWidth, height: panel.offsetHeight },
          getSafeInsets()
        )
      );
    },
    [enabled, getSafeInsets]
  );

  return {
    boundaryRef,
    panelRef,
    position,
    point,
    ready,
    canAnimatePosition,
    dragging,
    onDragPointerDown,
    onDragPointerMove,
    onDragPointerUp,
    moveByKey,
    shouldSuppressClick: () => suppressClickRef.current,
  };
}
